import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, ChevronDown, ChevronRight, Database, Pencil, Trash2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { useProjectStore } from '@/store/projectStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { deleteApplication, getDeleteDataPreview, saveDraft } from '@/api/workbench'
import type { DeleteDataPreview, DeleteDataTable } from '@/api/workbench'
import { clearAgentChatsForProject } from '@/lib/agentChatPersistence'

/**
 * The two irreversible-ish project actions the old Overview tab carried:
 * changing the Application ID (owner only — it renames the folder) and
 * deleting the application. Same API calls and confirmations as before; they
 * now live in App settings › Technical details (and on the invalid-project
 * screen).
 */

function errDetail(err: unknown, fallback: string): string {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof d === 'string' && d ? d : fallback
}

export function ChangeAppIdButton({ folderName, currentId }: { folderName: string; currentId: string }) {
  const perms = useProjectPermissions()
  const { fetchProjects, fetchScmInfo, clearCurrentProject } = useProjectStore()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(currentId)
  const [saving, setSaving] = useState(false)
  if (!perms.isOwner) return null
  const isDraft = folderName === 'Draft Application'

  const submit = async () => {
    const appId = value.trim()
    if (!appId) {
      toast.error('Please enter an application ID')
      return
    }
    setSaving(true)
    try {
      // Rename the folder the user is viewing (never assume the shared draft slot).
      await saveDraft(appId, folderName)
      toast.success('Application saved')
      setOpen(false)
      await fetchProjects()
      if (isDraft) clearCurrentProject()
      else fetchScmInfo(folderName)
    } catch (err) {
      toast.error(errDetail(err, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm"
        onClick={() => { setValue(currentId); setOpen(true) }}>
        <Pencil size={13} /> {isDraft ? 'Save the app' : 'Change ID'}
      </button>
      {open && (
        <Modal
          open
          onClose={() => { if (!saving) setOpen(false) }}
          title={isDraft ? 'Save application' : 'Change application ID'}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={saving || !value.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0, color: 'var(--color-text-secondary)' }}>
            {isDraft
              ? 'Confirm or update the application ID. The draft will be saved with this ID.'
              : 'Update the application ID.'}
          </p>
          <label style={{ display: 'block', marginBottom: 6, fontSize: '0.875rem', fontWeight: 600 }} htmlFor="app-id-input">
            Application ID
          </label>
          <input
            id="app-id-input"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) void submit() }}
          />
        </Modal>
      )}
    </>
  )
}

export function DeleteAppButton({ folderName }: { folderName: string }) {
  const perms = useProjectPermissions()
  const { fetchProjects, clearCurrentProject } = useProjectStore()
  const [open, setOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteData, setDeleteData] = useState(false)
  const [preview, setPreview] = useState<DeleteDataPreview | null>(null)
  const [previewError, setPreviewError] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPreview(null)
    setPreviewError('')
    getDeleteDataPreview(folderName)
      .then((p) => {
        if (cancelled) return
        setPreview(p)
        // Deleting the app deletes its tables unless the owner unticks it.
        setDeleteData(!p.own_database && !p.error && dataTables(p).length > 0)
      })
      .catch((err) => { if (!cancelled) setPreviewError(errDetail(err, 'Could not read the app\'s data')) })
    return () => { cancelled = true }
  }, [open, folderName])

  if (!perms.canDelete) {
    return (
      <p className="swb-muted" style={{ margin: 0 }}>Only the project owner can delete this app.</p>
    )
  }

  const submit = async () => {
    if (confirmText !== folderName) {
      toast.error('Project name does not match')
      return
    }
    setDeleting(true)
    try {
      const res = await deleteApplication(folderName, confirmText, deleteData)
      // Drop the persisted Ask-AI transcripts for this project so a future
      // same-name project starts with a clean console.
      clearAgentChatsForProject(folderName)
      toast.success(deleteData && res.dropped?.length
        ? `Application and ${res.dropped.length} data object(s) deleted`
        : 'Application deleted')
      for (const note of res.notes || []) toast(note, { duration: 8000 })
      setOpen(false)
      clearCurrentProject()
      fetchProjects()
    } catch (err) {
      toast.error(errDetail(err, 'Failed to delete'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingTop: 12, borderTop: '1px solid var(--color-surface-2)' }}>
      <span style={{ flex: '1 1 260px', fontSize: '0.875rem' }}>
        <strong>Delete this app.</strong>{' '}
        <span className="swb-muted">Every file, specification and generated code is removed. This can't be undone.</span>
      </span>
      <button type="button" className="swb-btn swb-btn-danger" onClick={() => { setConfirmText(''); setDeleteData(false); setOpen(true) }}>
        <Trash2 size={15} /> Delete app
      </button>
      {open && (
        <Modal
          open
          onClose={() => { if (!deleting) setOpen(false) }}
          title="Delete application"
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)} disabled={deleting}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={submit} disabled={deleting || confirmText !== folderName || !!preview?.marketplace_installs}>
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            This action is <strong style={{ color: 'var(--color-error)' }}>irreversible</strong>. All project
            files, specifications, and generated code will be permanently deleted.
          </p>
          <DeleteDataOption
            preview={preview}
            error={previewError}
            checked={deleteData}
            onChange={setDeleteData}
            disabled={deleting}
          />
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Type <strong style={{ color: 'var(--color-text)' }}>{folderName}</strong> to confirm:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={folderName}
            aria-label="Type the project name to confirm"
            style={{ borderColor: confirmText === folderName ? 'var(--color-accent)' : undefined }}
          />
        </Modal>
      )}
    </div>
  )
}

/** The tables a delete would drop: what exists now, or — when the database
 * could not be read — the app's designed tables. */
function dataTables(p: DeleteDataPreview): DeleteDataTable[] {
  if (p.live_error) return p.tables.map((name) => ({ name, rows: 0, rows_estimated: false }))
  return p.live_tables || []
}

const fmt = new Intl.NumberFormat()

function DeleteDataOption({ preview, error, checked, onChange, disabled }: {
  preview: DeleteDataPreview | null
  error: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled: boolean
}) {
  const [showList, setShowList] = useState(false)
  const box: React.CSSProperties = {
    margin: '12px 0', padding: '12px 14px', borderRadius: 6, fontSize: '0.875rem', lineHeight: 1.5,
    border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
  }
  const head: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start' }

  if (error) return <div style={box}>{error}</div>
  if (!preview) return <div style={box} className="swb-muted">Checking the app's tables…</div>
  if (preview.marketplace_installs > 0) {
    return (
      <div style={{ ...box, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
        {preview.marketplace_installs} organization(s) installed this app from the marketplace.
        Withdraw the listing and let those installs end before deleting it.
      </div>
    )
  }
  if (preview.error) return <div style={box}>{preview.error}</div>

  if (preview.own_database) {
    const ext = preview.external_database
    const where = ext && ext.kind !== 'unknown'
      ? [ext.name, ext.db_type && ext.db_type.toUpperCase(), ext.database, ext.host].filter(Boolean).join(' · ')
      : 'a database you connected'
    return (
      <div style={{ ...box, borderColor: 'var(--color-accent)' }} role="note">
        <div style={head}>
          <Database size={18} style={{ flex: 'none', marginTop: 1, color: 'var(--color-accent)' }} />
          <span>
            <strong>Your database tables will NOT be deleted.</strong>
            <span style={{ display: 'block' }}>
              This app keeps its data in an external database: <strong>{where}</strong>. Only the app
              itself is removed — every table and record in that database stays as it is.
            </span>
          </span>
        </div>
      </div>
    )
  }

  const oracle = preview.dialect === 'oracle'
  const ext = preview.external_database
  const oracleWhere = ext ? [ext.name, ext.database, ext.host].filter(Boolean).join(' · ') : 'your Oracle database'
  const tables = dataTables(preview)
  const count = tables.length
  const totalRows = tables.reduce((n, t) => n + (t.rows || 0), 0)
  const anyEstimate = tables.some((t) => t.rows_estimated)
  if (count === 0) {
    return (
      <div style={box} className="swb-muted">
        {oracle
          ? <>Twasta created no tables for this app in the Oracle database <strong>{oracleWhere}</strong>
            {preview.kept_not_created.length > 0 && <> — its {preview.kept_not_created.length} other
            table(s) were already there and are kept</>}. Nothing there will be deleted.</>
          : "This app has no tables in the database yet — nothing there to delete."}
      </div>
    )
  }
  const tableWord = `${fmt.format(count)} table${count === 1 ? '' : 's'}`
  const rowsWord = preview.live_error ? '' :
    ` with ${anyEstimate ? 'about ' : ''}${fmt.format(totalRows)} record${totalRows === 1 ? '' : 's'}`
  const warn = checked

  return (
    <div style={{ ...box, borderColor: warn ? 'var(--color-error)' : 'var(--color-border)' }}
      role={warn ? 'alert' : undefined}>
      <button type="button" onClick={() => setShowList((v) => !v)} aria-expanded={showList}
        style={{ ...head, width: '100%', padding: 0, border: 0, background: 'none', color: 'inherit',
          font: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
        <AlertTriangle size={18} style={{ flex: 'none', marginTop: 1, color: warn ? 'var(--color-error)' : 'var(--color-text-secondary)' }} />
        <span style={{ flex: 1 }}>
          <strong style={{ color: warn ? 'var(--color-error)' : undefined }}>
            {warn ? `${tableWord}${rowsWord} will be deleted` : `${tableWord}${rowsWord} will be kept`}
          </strong>
          <span className="swb-muted" style={{ display: 'block' }}>
            {oracle
              ? <>In your connected Oracle database <strong>{oracleWhere}</strong> (schema <code>{preview.schema.toUpperCase()}</code>) —
                only tables Twasta created for this app. They go to Oracle's recycle bin.</>
              : preview.drop_schema
                ? <>The app's own schema <code>{preview.schema}</code> is removed with everything in it.</>
                : <>From the organization's shared schema <code>{preview.schema}</code>.</>}
            {' '}{showList ? 'Hide the list' : 'Click to see the list'}.
          </span>
        </span>
        {showList ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {showList && (
        <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', borderTop: '1px solid var(--color-border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr className="swb-muted" style={{ textAlign: 'left' }}>
                <th style={{ padding: '6px 4px', fontWeight: 600 }}>Table</th>
                {!preview.live_error && <th style={{ padding: '6px 4px', fontWeight: 600, textAlign: 'right' }}>Records</th>}
              </tr>
            </thead>
            <tbody>
              {tables.map((t) => (
                <tr key={t.name} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '4px', fontFamily: 'var(--font-mono, monospace)' }}>{t.name}</td>
                  {!preview.live_error && (
                    <td style={{ padding: '4px', textAlign: 'right' }}>
                      {t.rows_estimated ? '~' : ''}{fmt.format(t.rows)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview.live_error && (
        <div className="swb-muted" style={{ marginTop: 6 }}>
          The database couldn't be reached, so this is the app's designed list — record counts are unknown.
        </div>
      )}
      {oracle && preview.kept_not_created.length > 0 && (
        <div className="swb-muted" style={{ marginTop: 6 }}>
          {preview.kept_not_created.length} other table(s) the app uses were not created by Twasta
          (they were already in the database) and are kept.
        </div>
      )}
      {preview.kept_shared.length > 0 && (
        <div className="swb-muted" style={{ marginTop: 6 }}>
          {preview.kept_shared.length} more table(s) are also used by another app of your organization
          and are kept.
        </div>
      )}
      {preview.other_organizations > 0 && (
        <div className="swb-muted" style={{ marginTop: 6 }}>
          {preview.other_organizations} other organization(s) using this app keep their own data.
        </div>
      )}

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} disabled={disabled}
          onChange={(e) => onChange(e.target.checked)} />
        <span>Delete these tables and their records</span>
      </label>
    </div>
  )
}
